const DELAYS = { focus: 100, focusReduced: 10, dropdown: 50, dropdownReduced: 10, close: 500 };
const SELECTORS = {
  focusable: 'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])',
  dropdownLinks: 'a[tabindex="0"]',
  megaMenuLinks: '.section-header-desktop-mega-menu-featured a',
  aswWidget: '.asw-widget'
};

class FocusManager {
  static trap(container, menuButton = null) {
    const elements = container.querySelectorAll(SELECTORS.focusable);
    if (!elements.length) return null;
    
    const [first, last] = [elements[0], elements[elements.length - 1]];
    
    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        if (menuButton) {
          menuButton.focus();
        } else {
          first.focus();
        }
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        if (menuButton) {
          menuButton.focus();
        } else {
          last.focus();
        }
      }
    });
    
    return first;
  }

  static delayed(element, delay) {
    if (element) setTimeout(() => element.focus(), delay);
  }
}

const aria = (element, attrs) => {
  if (!element) return;
  Object.entries(attrs).forEach(([key, value]) => 
    value !== null && element.setAttribute(`aria-${key}`, value)
  );
};

class Modal {
  constructor(element, button, overlay) {
    Object.assign(this, { element, button, overlay, lastFocus: null });
  }

  isOpen() {
    return this.element?.classList.contains('open') || this.element?.style.display === 'flex';
  }

  open(onOpen) {
    this.lastFocus = document.activeElement;
    const isSearch = this.element.classList.contains('section-header-search-aside');
    
    isSearch ? this.element.style.display = 'flex' : this.element.classList.add('open');
    aria(this.element, { hidden: 'false' });
    aria(this.button, { expanded: 'true' });
    
    onOpen?.();
  }

  close(onClose) {
    const isSearch = this.element.classList.contains('section-header-search-aside');
    
    isSearch ? this.element.style.display = 'none' : this.element.classList.remove('open');
    aria(this.element, { hidden: 'true' });
    aria(this.button, { expanded: 'false' });
    
    onClose?.();
    
    if (this.lastFocus === this.button) this.button.focus();
  }

  toggle(onOpen, onClose) {
    this.isOpen() ? this.close(onClose) : this.open(onOpen);
  }
}

class SectionHeader extends HTMLElement {
  constructor() {
    super();
    this.dropdownTimeouts = new Map();
    this.enableArrowNavInDropdowns = true;
  }

  connectedCallback() {
    this.animationsDisabled = this.hasAttribute('data-no-animations') || 
                             window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    this.initElements();
    this.initControllers();
    this.initEventListeners();
    this.initCartObserver();
  }

  initElements() {
    this.mobile = {
      button: this.querySelector('.section-header-mobile-menu-button'),
      drawer: this.querySelector('.section-header-mobile-drawer'),
      overlay: this.querySelector('.section-header-mobile-drawer-overlay'),
      backButton: this.querySelector('.section-header-mobile-drawer-back'),
      mainNav: this.querySelector('.section-header-mobile-drawer-navigation'),
      childNavs: this.querySelectorAll('.section-header-mobile-drawer-child-navigation'),
      parentButtons: this.querySelectorAll('.section-header-mobile-drawer-navigation-list-item-button')
    };

    this.search = {
      button: this.querySelector('.section-header-action-button[aria-controls="header-search-modal"]'),
      aside: this.querySelector('.section-header-search-aside'),
      closeButton: this.querySelector('.section-header-search-close'),
      input: this.querySelector('.section-header-search-input'),
      globalOverlay: document.querySelector('.header-search-overlay')
    };

    this.desktop = {
      dropdownButtons: this.querySelectorAll('[data-dropdown-trigger]'),
      navList: this.querySelector('.section-header-desktop-navigation-list')
    };
  }

  initControllers() {
    if (this.mobile.drawer && this.mobile.button) {
      this.mobileModal = new Modal(this.mobile.drawer, this.mobile.button, this.mobile.overlay);
    }

    if (this.search.aside && this.search.button) {
      this.searchModal = new Modal(this.search.aside, this.search.button, this.search.globalOverlay);
    }
  }

  initEventListeners() {
    this.initMobileNav();
    this.initDesktopNav();
    this.initSearch();
    this.initGlobalHandlers();
  }

  getDelay(type = 'dropdown') {
    const key = type === 'focus' ? 'focus' : 'dropdown';
    return this.animationsDisabled ? DELAYS[`${key}Reduced`] : DELAYS[key];
  }

  togglePageLock(lock) {
    document.body.style.overflow = lock ? 'hidden' : '';
    const main = document.querySelector('main');
    main?.setAttribute('inert', lock ? '' : null);
    main?.removeAttribute('inert');
  }

  toggleWidget(show) {
    const widget = document.querySelector(SELECTORS.aswWidget);
    if (widget) widget.style.display = show ? '' : 'none';
  }

  initMobileNav() {
    const { button, drawer, backButton, parentButtons } = this.mobile;
    if (!button || !drawer) return;

    button.addEventListener('click', () => 
      this.mobileModal.isOpen() ? this.closeMobileMenu() : this.openMobileMenu()
    );
    
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer || e.target === this.mobile.overlay) this.closeMobileMenu();
    });

    backButton?.addEventListener('click', () => this.showMainNav());
    
    backButton?.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        const activeNav = Array.from(this.mobile.childNavs).find(nav => nav.style.display === 'flex');
        if (activeNav) {
          const firstNavItem = activeNav.querySelector('a, button');
          firstNavItem?.focus();
        }
      } else if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        this.mobile.button.focus();
      }
    });

    parentButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const menuId = btn.dataset.menuId;
        if (menuId) this.showChildNav(menuId);
      });
    });
  }

  openMobileMenu() {
    this.mobileModal.open(() => {
      this.mobile.button.classList.add('menu-open');
      aria(this.mobile.button, { label: 'Close navigation menu' });
      this.togglePageLock(true);
      this.toggleWidget(false);
      
      setTimeout(() => {
        FocusManager.trap(this.mobile.drawer, this.mobile.button);
        
        this.mobile.button.addEventListener('keydown', this.handleMenuButtonTab = (e) => {
          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            if (this.mobile.backButton && this.mobile.backButton.style.display === 'flex') {
              this.mobile.backButton.focus();
            } else {
              const firstNavItem = this.mobile.drawer.querySelector('.section-header-mobile-drawer-navigation a, .section-header-mobile-drawer-navigation button');
              firstNavItem?.focus();
            }
          } else if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const allItems = this.mobile.drawer.querySelectorAll(SELECTORS.focusable);
            const lastItem = allItems[allItems.length - 1];
            lastItem?.focus();
          }
        });
      }, this.getDelay('focus'));
    });
  }

  closeMobileMenu() {
    this.mobileModal.close(() => {
      this.mobile.button.classList.remove('menu-open');
      aria(this.mobile.button, { label: 'Open navigation menu' });
      this.togglePageLock(false);
      this.toggleWidget(true);
      this.showMainNav();
      
      if (this.handleMenuButtonTab) {
        this.mobile.button.removeEventListener('keydown', this.handleMenuButtonTab);
        this.handleMenuButtonTab = null;
      }
    });
  }

  showMainNav() {
    const { backButton, mainNav, childNavs } = this.mobile;
    if (backButton) backButton.style.display = 'none';
    if (mainNav) mainNav.style.display = 'block';
    childNavs.forEach(nav => nav.style.display = 'none');
  }

  showChildNav(menuId) {
    const { backButton, mainNav, childNavs } = this.mobile;
    
    if (backButton) {
      backButton.style.display = 'flex';
      backButton.focus();
    }
    if (mainNav) mainNav.style.display = 'none';

    childNavs.forEach(nav => {
      const isActive = nav.dataset.childMenu === menuId;
      nav.style.display = isActive ? 'flex' : 'none';
      
      if (isActive) {
        setTimeout(() => FocusManager.trap(this.mobile.drawer, this.mobile.button), this.getDelay('dropdown'));
      }
    });
  }

  initDesktopNav() {
    this.desktop.dropdownButtons.forEach(button => this.setupDropdown(button));
  }

  setupDropdown(button) {
    const listItem = button.closest('.section-header-desktop-navigation-list-item');
    const dropdown = listItem?.querySelector('.section-header-desktop-navigation-dropdown');
    if (!dropdown) return;

    const handlers = this.createDropdownHandlers(button, dropdown);

    button.addEventListener('mouseenter', handlers.open);
    listItem.addEventListener('mouseleave', handlers.close);
    dropdown.addEventListener('mouseenter', handlers.cancelClose);
    button.addEventListener('click', handlers.toggle);
    button.addEventListener('focus', handlers.open);
    button.addEventListener('keydown', (e) => this.handleDropdownKeys(e, button, dropdown, handlers));
    dropdown.addEventListener('keydown', (e) => this.handleDropdownNavKeys(e, button, dropdown, handlers));

    this.setupImageZoom(dropdown);
  }

  createDropdownHandlers(button, dropdown) {
    const id = `${button.id || Math.random()}`;
    
    return {
      open: () => {
        clearTimeout(this.dropdownTimeouts.get(id));
        this.closeAllDropdowns(dropdown);
        dropdown.classList.add('show');
        aria(button, { expanded: 'true' });
        this.setTabIndex(dropdown, '0');
      },
      close: () => {
        const timeout = setTimeout(() => {
          dropdown.classList.remove('show');
          aria(button, { expanded: 'false' });
          this.setTabIndex(dropdown, '-1');
        }, DELAYS.close);
        this.dropdownTimeouts.set(id, timeout);
      },
      cancelClose: () => clearTimeout(this.dropdownTimeouts.get(id)),
      toggle: (e) => {
        e?.preventDefault();
        dropdown.classList.contains('show') ? handlers.close() : handlers.open();
      }
    };
  }

  setTabIndex(dropdown, value) {
    [...dropdown.querySelectorAll('a'), 
     ...dropdown.querySelectorAll(SELECTORS.megaMenuLinks)]
      .forEach(link => link.setAttribute('tabindex', value));
  }

  handleDropdownKeys(e, button, dropdown, handlers) {
    const actions = {
      'Enter': () => handlers.toggle(e),
      ' ': () => handlers.toggle(e),
      'Escape': () => dropdown.classList.contains('show') && handlers.close(),
      'ArrowDown': () => {
        if (dropdown.classList.contains('show')) {
          e.preventDefault();
          dropdown.querySelector(SELECTORS.dropdownLinks)?.focus();
        }
      }
    };

    if (actions[e.key]) {
      e.preventDefault();
      actions[e.key]();
    }
  }

  handleDropdownNavKeys(e, button, dropdown, handlers) {
    if (e.key === 'Escape') {
      e.preventDefault();
      button.focus();
      handlers.close();
      return;
    }
    
    if (this.enableArrowNavInDropdowns && ['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const links = [
        ...dropdown.querySelectorAll(SELECTORS.dropdownLinks),
        ...dropdown.querySelectorAll(SELECTORS.megaMenuLinks)
      ];
      const current = links.indexOf(document.activeElement);
      
      const next = e.key === 'ArrowDown' 
        ? (current + 1) % links.length
        : current > 0 ? current - 1 : links.length - 1;
      
      links[next]?.focus();
    }
    
    if (e.key === 'Tab') {
      const links = dropdown.querySelectorAll('a[tabindex="0"]');
      const [first, last] = [links[0], links[links.length - 1]];
      
      setTimeout(() => {
        if ((e.shiftKey && document.activeElement === button) ||
            (!e.shiftKey && !dropdown.contains(document.activeElement))) {
          handlers.close();
        }
      }, 0);
    }
  }

  setupImageZoom(dropdown) {
    const navItems = dropdown.querySelectorAll('.section-header-desktop-navigation-dropdown-item a');
    const megaItems = dropdown.querySelectorAll('.section-header-desktop-mega-menu-featured');

    navItems.forEach(navLink => {
      navLink.addEventListener('mouseenter', () => {
        const navUrl = navLink.getAttribute('href');
        const navText = navLink.textContent.trim().toLowerCase().replace(/\s+/g, ' ');

        megaItems.forEach(megaItem => {
          const megaLink = megaItem.querySelector('a');
          const megaTitle = megaItem.querySelector('h3');
          const megaImage = megaItem.querySelector('img');
          
          if (!megaImage) return;
          
          const shouldHighlight = 
            (megaLink && navUrl && megaLink.getAttribute('href') === navUrl) ||
            (megaTitle && megaTitle.textContent.trim().toLowerCase().replace(/\s+/g, ' ') === navText);
          
          megaImage.classList.toggle('nav-hovered', shouldHighlight);
        });
      });

      navLink.addEventListener('mouseleave', () => {
        megaItems.forEach(item => 
          item.querySelector('img')?.classList.remove('nav-hovered')
        );
      });
    });
  }

  closeAllDropdowns(exclude = null) {
    this.querySelectorAll('.section-header-desktop-navigation-dropdown.show').forEach(dropdown => {
      if (dropdown === exclude) return;
      
      dropdown.classList.remove('show');
      const button = dropdown.closest('.section-header-desktop-navigation-list-item')?.querySelector('button');
      if (button) aria(button, { expanded: 'false' });
      this.setTabIndex(dropdown, '-1');
    });
  }

  initSearch() {
    const { button, aside, closeButton, globalOverlay, input } = this.search;
    if (!button || !aside) return;

    button.addEventListener('click', () => this.openSearch());
    closeButton?.addEventListener('click', () => this.closeSearch());
    globalOverlay?.addEventListener('click', () => this.closeSearch());

    if (input) this.setupSearchKeys();
  }

  openSearch() {
    this.searchModal.open(() => {
      aria(this.search.button, { label: 'Close search' });
      this.search.globalOverlay?.classList.add('show');
      this.togglePageLock(true);
      
      setTimeout(() => {
        this.search.input?.focus();
        FocusManager.trap(this.search.aside);
      }, this.getDelay('focus'));
    });
  }

  closeSearch() {
    this.searchModal.close(() => {
      aria(this.search.button, { label: 'Open search' });
      this.search.globalOverlay?.classList.remove('show');
      this.togglePageLock(false);
      this.clearSearch();
    });
  }

  clearSearch() {
    if (this.search.input) this.search.input.value = '';
    const results = this.querySelector('.section-header-search-results');
    if (results) {
      results.innerHTML = '';
      results.style.display = 'none';
    }
  }

  setupSearchKeys() {
    let selectedIndex = -1;

    this.search.input.addEventListener('keydown', (e) => {
      const results = this.querySelectorAll('.section-header-search-result-item');
      
      const actions = {
        'ArrowDown': () => selectedIndex < results.length - 1 && selectedIndex++,
        'ArrowUp': () => selectedIndex > -1 && selectedIndex--,
        'Enter': () => results[selectedIndex]?.querySelector('a')?.click()
      };

      if (actions[e.key]) {
        e.preventDefault();
        actions[e.key]();
        this.updateSearchSelection(results, selectedIndex);
      }
    });

    this.search.input.addEventListener('input', () => {
      selectedIndex = -1;
      aria(this.search.input, { expanded: this.search.input.value.length > 0 });
    });
  }

  updateSearchSelection(results, index) {
    results.forEach((result, i) => {
      const selected = i === index;
      aria(result, { selected: selected.toString() });
      if (selected) result.querySelector('a')?.focus();
    });
  }

  initGlobalHandlers() {
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;

      if (this.search.aside?.style.display === 'flex') {
        this.closeSearch();
      } else if (this.mobile.drawer?.classList.contains('open')) {
        this.closeMobileMenu();
      } else {
        this.closeAllDropdowns();
      }
    });

    document.addEventListener('focusin', (e) => {
      const focusedElement = e.target;
      
      if (this.mobile.drawer?.classList.contains('open')) {
        if (!this.mobile.drawer.contains(focusedElement) && 
            focusedElement !== this.mobile.button &&
            !this.contains(focusedElement)) {
          this.closeMobileMenu();
        }
      }
      
      if (this.search.aside?.style.display === 'flex') {
        if (!this.search.aside.contains(focusedElement) && 
            focusedElement !== this.search.button &&
            !this.contains(focusedElement)) {
          this.closeSearch();
        }
      }
      
      const openDropdown = this.querySelector('.section-header-desktop-navigation-dropdown.show');
      if (openDropdown) {
        const dropdownButton = openDropdown.closest('.section-header-desktop-navigation-list-item')?.querySelector('button');
        if (!openDropdown.contains(focusedElement) && 
            focusedElement !== dropdownButton &&
            this.contains(focusedElement)) {
          this.closeAllDropdowns();
        }
      }
    });
  }

  initCartObserver() {
    const cartBubble = document.getElementById('cart-icon-bubble');
    if (!cartBubble) return;

    new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type !== 'childList') return;
        const count = cartBubble.querySelector('.cart-count-bubble span:not(.visually-hidden)')?.textContent;
        if (count) this.announceCart(count);
      });
    }).observe(cartBubble, { childList: true, subtree: true });
  }

  announceCart(count) {
    const announcement = document.createElement('div');
    aria(announcement, { live: 'polite' });
    announcement.setAttribute('role', 'status');
    announcement.classList.add('visually-hidden');
    announcement.textContent = `Cart updated: ${count} item${count !== '1' ? 's' : ''}`;
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  }
}

customElements.define('section-header', SectionHeader);